import { useRef, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    ToolbarSpacer,
    TSTAdjustView,
    TSTTable,
    TableToolbar,
    TSTFilterBar,
    TextTool,
    TSTPagination,
    useReplaceSearchParams,
} from "@impero-com/ui";
import {
    supportsContainsFilter,
    supportsOnlyContainsFilter,
    CONTROL_ACTIVITIES_LIST_COLUMN_IDS,
} from "@impero-com/ui/domain";
import { Spin } from "antd";

import { ExistingControlResultPaginatedListDto as ControlResultPaginatedListDto } from "api/generated/model";
import { UserTagCategoryOverview } from "api/generated/model/userTagCategoryOverview";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";
import {
    getContainsOnlyFilterModeConfig,
    getFilterModeConfig,
    getStringFilterModeConfig,
} from "utils/table/getFilterModeConfig";

import { ControlActivityArchiveModal } from "./components/ControlActivityArchiveModal";
import { ControlActivityExportModal } from "./components/ControlActivityExportModal";
import { ControlActivityExtendDueDateModal } from "./components/ControlActivityExtendDueDateModal";
import { ControlActivityReassignModal } from "./components/ControlActivityReassignModal";
import { ControlActivityReminderModal } from "./components/ControlActivityReminderModal";
import { ControlActivityReopenModal } from "./components/ControlActivityReopenModal";
import { ControlActivityUnarchiveModal } from "./components/ControlActivityUnarchiveModal";
import { ControlActivitiesScope } from "./consts";
import { ControlActivitiesArchivedFilterRow } from "./ControlActivitiesArchivedFilterRow";
import { ControlActivitiesDateFilterRow } from "./ControlActivitiesDateFilterRow";
import { ControlActivityTimelineModal } from "./ControlActivityTimelineModal";
import { ControlActivityTableRow } from "./types";
import { ControlActivitiesArchivedFilters } from "./useControlActivitiesArchivedFilter";
import { ControlActivitiesDataParamsApi } from "./useControlActivitiesDataParams";
import { ControlActivitiesDateFilters } from "./useControlActivitiesDateFilters";
import { useControlActivitiesListTable } from "./useControlActivitiesListTable";
import { useControlActivityArchiveUtils } from "./useControlActivityArchiveUtils";
import { useControlActivityExportUtils } from "./useControlActivityExportUtils";
import { useControlActivityExtendDueDateUtils } from "./useControlActivityExtendDueDateUtils";
import { useControlActivityReassignUtils } from "./useControlActivityReassignUtils";
import { useControlActivityReminderUtils } from "./useControlActivityReminderUtils";
import { useControlActivityReopenUtils } from "./useControlActivityReopenUtils";
import { useControlActivityUnarchiveUtils } from "./useControlActivityUnarchiveUtils";

interface ControlActivitiesListProps {
    controlResultsDto: ControlResultPaginatedListDto;
    tagCategories: UserTagCategoryOverview[];
    dataParams: ControlActivitiesDataParamsApi;
    dateFilters: ControlActivitiesDateFilters;
    archivedFilter: ControlActivitiesArchivedFilters;
    isTableLoading: boolean;
    /** Determines the scope of the list, which affects the default filters and the storage key for the table state. */
    scope: ControlActivitiesScope;
}

export function ControlActivitiesList({
    controlResultsDto,
    tagCategories,
    dataParams,
    dateFilters,
    archivedFilter,
    isTableLoading,
    scope,
}: ControlActivitiesListProps) {
    const { t } = useTranslation();
    const [timelineActivity, setTimelineActivity] =
        useState<ControlActivityTableRow | null>(null);
    const {
        table,
        getFilterComponent,
        canAddException,
        tableId,
        clearColumnFilters,
        resetPersistedColumnFilters,
        dueDateBounds,
    } = useControlActivitiesListTable(
        controlResultsDto,
        tagCategories,
        setTimelineActivity,
        scope
    );
    const replaceSearchParams = useReplaceSearchParams();

    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const visibleColumns = table
        .getAllColumns()
        .filter((column) => column.getIsVisible());

    const deselectAll = () => table.resetRowSelection();

    const {
        exportToolState,
        exportModalOpen,
        openExportModal,
        closeExportModal,
        handleExport,
        isExporting,
    } = useControlActivityExportUtils({
        selectedRows,
        visibleColumns,
    });

    const {
        reminderToolState,
        reminderModalOpen,
        openReminderModal,
        closeReminderModal,
        handleRemind,
        isReminding,
    } = useControlActivityReminderUtils({
        selectedRows,
        deselectAll,
    });

    const {
        archiveToolState,
        archiveModalOpen,
        openArchiveModal,
        closeArchiveModal,
        handleArchive,
        isArchiving,
    } = useControlActivityArchiveUtils({
        selectedRows,
        deselectAll,
    });

    const {
        unarchiveToolState,
        unarchiveModalOpen,
        openUnarchiveModal,
        closeUnarchiveModal,
        handleUnarchive,
        isUnarchiving,
    } = useControlActivityUnarchiveUtils({
        selectedRows,
        deselectAll,
    });

    const {
        reassignToolState,
        reassignModalOpen,
        openReassignModal,
        closeReassignModal,
        handleReassign,
        isReassigning,
        reviewerCount,
    } = useControlActivityReassignUtils({
        selectedRows,
        deselectAll,
    });

    const {
        reopenToolState,
        selectionDueDates: reopenSelectionDueDates,
        singleActivityTitle,
        reopenModalOpen,
        openReopenModal,
        closeReopenModal,
        handleReopen,
        isReopening,
    } = useControlActivityReopenUtils({
        selectedRows,
        deselectAll,
    });

    const extendDueDateEnabled = useFeatureFlag("activityDueDateExtension");
    const {
        extendDueDateToolState,
        selectionDueDates,
        extendDueDateModalOpen,
        openExtendDueDateModal,
        closeExtendDueDateModal,
        handleExtendDueDate,
        isExtendingDueDate,
    } = useControlActivityExtendDueDateUtils({
        selectedRows,
        deselectAll,
    });

    const tableRef = useRef<HTMLTableElement>(null);

    const setModeConfig = getFilterModeConfig(t);
    const stringModeConfig = getStringFilterModeConfig(t);
    const containsOnlyModeConfig = getContainsOnlyFilterModeConfig(t);
    const getModeConfig = (column: { id: string }) => {
        if (column.id === CONTROL_ACTIVITIES_LIST_COLUMN_IDS.TIME_TO_DUE_DATE) {
            return undefined;
        }
        if (supportsOnlyContainsFilter(column.id)) {
            return containsOnlyModeConfig;
        }
        return supportsContainsFilter(column.id)
            ? stringModeConfig
            : setModeConfig;
    };

    // Count the date filter only when it's been changed from its default
    // (due-date 2-month window); the default doesn't count as an active filter.
    const dateFilterCount = dateFilters.isDefault ? 0 : 1;
    const isArchivedNonDefault =
        !archivedFilter.active || archivedFilter.archived;
    const extraFilterCount = (isArchivedNonDefault ? 1 : 0) + dateFilterCount;

    // "Clear all" restores every filter to its default: wipe the column-filter
    // and data-param URL keys in one nav; the stored data params follow the URL.
    const handleClearAllFilters = () => {
        replaceSearchParams((params: URLSearchParams) => {
            clearColumnFilters(params);
            dataParams.deleteKeysFromQueryParams(params);
        });
        resetPersistedColumnFilters();
    };
    return (
        <div>
            <ControlActivityExportModal
                onClose={closeExportModal}
                onSubmit={(type) => void handleExport(type)}
                open={exportModalOpen}
                loading={isExporting}
            />

            <ControlActivityReminderModal
                open={reminderModalOpen}
                loading={isReminding}
                onConfirm={(customMessage) => void handleRemind(customMessage)}
                onClose={closeReminderModal}
            />

            <ControlActivityArchiveModal
                open={archiveModalOpen}
                loading={isArchiving}
                onConfirm={(comment) => void handleArchive(comment)}
                onClose={closeArchiveModal}
            />

            <ControlActivityUnarchiveModal
                open={unarchiveModalOpen}
                loading={isUnarchiving}
                onConfirm={(comment) => void handleUnarchive(comment)}
                onClose={closeUnarchiveModal}
            />

            <ControlActivityReassignModal
                open={reassignModalOpen}
                loading={isReassigning}
                reviewerCount={reviewerCount}
                onConfirm={(newAssignees) => void handleReassign(newAssignees)}
                onClose={closeReassignModal}
            />

            <ControlActivityReopenModal
                open={reopenModalOpen}
                loading={isReopening}
                selectionCount={selectedRows.length}
                selectionDueDates={reopenSelectionDueDates}
                singleActivityTitle={singleActivityTitle}
                canPostponeDueDate={extendDueDateEnabled}
                onConfirm={(comment, newDueDay) =>
                    void handleReopen(comment, newDueDay)
                }
                onClose={closeReopenModal}
            />

            {extendDueDateEnabled && (
                <ControlActivityExtendDueDateModal
                    open={extendDueDateModalOpen}
                    loading={isExtendingDueDate}
                    selectionCount={selectedRows.length}
                    selectionDueDates={selectionDueDates}
                    onConfirm={(newDueDay, comment) =>
                        void handleExtendDueDate(newDueDay, comment)
                    }
                    onClose={closeExtendDueDateModal}
                />
            )}

            <ControlActivityTimelineModal
                activity={timelineActivity}
                onClose={() => setTimelineActivity(null)}
            />

            <TSTFilterBar<ControlActivityTableRow>
                table={table}
                tableId={tableId}
                addLabel={t("FilterBar_AddFilter")}
                globalSearchLabel={t("FilterBar_GlobalSearchPlaceholder")}
                getFilterCountLabel={(count) =>
                    t("FilterBar_FiltersApplied", { count })
                }
                rowCountLabel={t("FilterBar_RowCount", {
                    count: table.getPrePaginationRowModel().rows.length,
                })}
                hideFiltersLabel={t("FilterBar_HideFilters")}
                clearAllLabel={t("FilterBar_ClearAll")}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                addExceptionLabel={t("FilterBar_AddException")}
                getModeConfig={getModeConfig}
                aboveFilters={
                    <ControlActivitiesDateFilterRow
                        dateFilters={dateFilters}
                        dueDateBounds={dueDateBounds}
                    />
                }
                belowFilters={
                    <ControlActivitiesArchivedFilterRow
                        archivedFilters={archivedFilter}
                    />
                }
                extraFilterCount={extraFilterCount}
                onClearFilters={handleClearAllFilters}
            />
            <Spin spinning={isTableLoading} size="large">
                <TSTTable
                    tableRef={tableRef}
                    table={table}
                    noResultsFallback={t("NoResults")}
                    noSourceRowsFallback={t("NoResults")}
                    toolbar={
                        <TableToolbar>
                            <TextTool
                                state={reminderToolState}
                                onClick={openReminderModal}
                            >
                                {t("ControlResult_Remind")}
                            </TextTool>
                            <TextTool
                                state={reassignToolState}
                                onClick={openReassignModal}
                            >
                                {t("ControlResult_Reassign")}
                            </TextTool>
                            <TextTool
                                state={reopenToolState}
                                onClick={openReopenModal}
                            >
                                {t("ControlResult_Reopen")}
                            </TextTool>
                            <TextTool
                                state={archiveToolState}
                                onClick={openArchiveModal}
                            >
                                {t("ControlResult_Archive")}
                            </TextTool>
                            <TextTool
                                state={unarchiveToolState}
                                onClick={openUnarchiveModal}
                            >
                                {t("ControlResult_Unarchive")}
                            </TextTool>
                            <TextTool
                                state={exportToolState}
                                onClick={openExportModal}
                            >
                                {t("ControlResult_Export")}
                            </TextTool>
                            {extendDueDateEnabled && (
                                <TextTool
                                    state={extendDueDateToolState}
                                    onClick={openExtendDueDateModal}
                                >
                                    {t("ControlActivitiesList_ExtendDueDate")}
                                </TextTool>
                            )}
                            <ToolbarSpacer />
                            <TSTAdjustView
                                table={table}
                                columnsLabel={t("AdjustView_ColumnsHeader")}
                                reorderHintLabel={t(
                                    "AdjustView_DragToReorderHint"
                                )}
                                resetLabel={t("AdjustView_Reset")}
                                pinLabel={t("AdjustView_PinColumn")}
                                unpinLabel={t("AdjustView_UnpinColumn")}
                                containerRef={tableRef}
                            >
                                {t("ControlResult_AdjustView")}
                            </TSTAdjustView>
                        </TableToolbar>
                    }
                />
            </Spin>
            <TSTPagination
                table={table}
                tableElement={tableRef}
                pageLabel={t("Pagination_Page")}
                getRowCountLabel={(shown, count) =>
                    t("Pagination_RowCount", { shown, count })
                }
                getSinglePageRowCountLabel={(count) =>
                    t("Pagination_RowCountSinglePage", { count })
                }
            />
        </div>
    );
}
